import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/store/auth';
import { track } from '@/lib/posthog';
import { Loader2 } from 'lucide-react';

export function Onboarding() {
  const [fullName, setFullName] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const navigate = useNavigate();

  // Check if user already has a name set - if so, skip this step
  useEffect(() => {
    const checkExistingData = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user?.user_metadata?.full_name || user?.user_metadata?.name) {
          // User already has a name, skip to workspace setup (onboarding flow)
          navigate('/workspace/create?onboarding=true', { replace: true });
          return;
        }
      } catch (err) {
        console.error('Error checking user data:', err);
      } finally {
        setIsChecking(false);
      }
    };

    checkExistingData();
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      // Update user metadata with full name
      const { error: updateError } = await supabase.auth.updateUser({
        data: {
          full_name: fullName,
          name: fullName,
          onboarded: true,
        }
      });

      if (updateError) throw updateError;

      // Sync the auth store so downstream pages (e.g., WorkspaceSetup) see
      // the new name immediately — supabase.auth.updateUser does not flow
      // through the store on its own.
      const { user: currentUser, setUser } = useAuthStore.getState();
      if (currentUser) {
        setUser({ ...currentUser, name: fullName });
      }

      track('onboarding_profile_completed');

      // Navigate to workspace creation (onboarding flow)
      navigate('/workspace/create?onboarding=true');
    } catch (err: any) {
      setError(err.message || 'Failed to save your information');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  // Show loading while checking existing data
  if (isChecking) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-ingesta-blue-50 to-ingesta-teal-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-ingesta-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-ingesta-blue-50 to-ingesta-teal-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md mx-auto">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold text-ingesta-blue-600">
            Welcome to Ingesta!
          </CardTitle>
          <CardDescription>
            Let's get to know you
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="fullName" className="text-sm font-medium">
                Full Name
              </label>
              <Input
                id="fullName"
                type="text"
                placeholder="Enter your full name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
                autoFocus
              />
            </div>
            {error && (
              <div className="text-red-600 text-sm text-center">{error}</div>
            )}
            <Button
              type="submit"
              className="w-full"
              disabled={isLoading || !fullName.trim()}
            >
              {isLoading ? 'Saving...' : 'Continue'}
            </Button>
          </form>
          <div className="mt-4 text-center">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setShowLogoutDialog(true)}
              className="text-sm text-muted-foreground hover:text-foreground"
            >
              Sign out
            </Button>
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure you want to logout?</AlertDialogTitle>
            <AlertDialogDescription>
              You haven't finished your onboarding yet. You can complete this later by signing back in.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleLogout}>
              Sign out
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
